import { SetModelJsonResource } from "@/types/setTypes";
import { displayWeight, formatFloat } from "@/lib/utils";
import { displayClaimedHourMinOnly } from "@/lib/dateUtils";
import { UserModelJsonResource } from "@/types/userTypes";
import { WorkoutModelJsonResource } from "@/types/workoutTypes";
import { ExerciseModelJsonResource } from "@/types/exerciseTypes";

interface MateExerciseCardProps {
  exercise: ExerciseModelJsonResource;
  workout: WorkoutModelJsonResource;
  sets: SetModelJsonResource[];
  user: UserModelJsonResource | null;
}

/**
 * Read-only exercise card for displaying mate's exercises
 * Similar to OneExerciseCard but without delete/edit functionality
 */
export function MateExerciseCard({
  exercise,
  workout,
  sets,
  user,
}: MateExerciseCardProps) {
  // Calculate exercise volume percentage
  const exerciseVolumePercent = formatFloat(
    ((exercise._total_volume_lb ?? 0) / (workout.volume_lb ?? 1)) * 100,
    1
  );

  return (
    <div className="relative w-full">
      {/* Vertical line */}
      <div
        aria-hidden="true"
        className="absolute left-5 top-6 -ml-px mt-0.5 h-full w-[1px] bg-primary/30"
      />

      <div aria-current="step" className="group relative flex items-start">
        {/* Circle dot with percentage */}
        <span aria-hidden="true" className="flex h-9 items-center mt-1">
          <span className="relative z-12 flex size-9 items-center justify-center rounded-full border-2 border-primary bg-white">
            <span className="text-[10px]">{exerciseVolumePercent}%</span>
          </span>
        </span>

        {/* Exercise content */}
        <div className="ml-4 flex-1 min-w-0 flex-col">
          <div key={exercise.nano_id} className="flex flex-col gap-1 py-2">
            <div className="flex justify-between items-center mb-2">
              {/* Exercise name - read-only, no link */}
              <h4 className="font-medium text-base text-primary">
                {exercise.name}
              </h4>

              {/* Exercise total volume */}
              <h4 className="text-xs rounded-sm text-tx-default bg-accent-bg-cool/10 px-2 py-0.5">
                {displayWeight(
                  exercise._total_volume_lb ?? 0,
                  exercise._total_volume_kg ?? 0,
                  user?.preferred_weight_unit
                )}
              </h4>
            </div>

            {/* Sets list - read-only */}
            {sets
              .filter((set) => exercise._set_nano_ids?.includes(set.nano_id))
              .map((set) => (
                <div
                  key={set.nano_id}
                  className={
                    "flex items-center justify-between px-2 py-1 rounded-md border " +
                    (set.is_latest
                      ? " border-accent-bg-warm bg-accent-bg-warm/10"
                      : " border-bd-overlay bg-white ")
                  }
                >
                  {/* Set details - read-only */}
                  <div className="flex-1 items-center">
                    <div className="flex items-center justify-start">
                      <span className="text-tx-default/70 rounded-md bg-bg-base px-2 py-1 text-sm">
                        {displayClaimedHourMinOnly(set.claimed_train_at)}
                      </span>
                      <span className="inline-flex px-2 py-1 font-medium">
                        {displayWeight(
                          set.weight_lb,
                          set.weight_kg,
                          user?.preferred_weight_unit
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Reps display - read-only */}
                  <div className="text-tx-dark inline-flex items-center px-2">
                    <span className="text-sm font-medium">{set.reps}</span>
                    <span className="text-xs text-tx-muted ml-1">reps</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
